import React, { useEffect, useState } from 'react';
import {
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination,
  Paper, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  MenuItem, 
  Checkbox, 
  FormControl, 
  InputLabel, 
  Select, 
  OutlinedInput, 
  ListItemText, 
  Box, 
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Grid,
  Divider,
} from '@mui/material';
import { 
  Edit, 
  Delete, 
  Add,
  Code as CodeIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Settings as SettingsIcon,
  TextFields as StaticIcon,
  QuestionAnswer as PromptedIcon,
  CompareArrows as ConditionalIcon,
  Link as ChainedIcon,
} from '@mui/icons-material';
import apiService from '../services/api';

const macroTypes = [
  { value: 'static', label: 'Static Macro', icon: <StaticIcon />, color: '#1976d2' },
  { value: 'prompted', label: 'Prompted Macro', icon: <PromptedIcon />, color: '#388e3c' },
  { value: 'conditional', label: 'Conditional Macro', icon: <ConditionalIcon />, color: '#f57c00' },
  { value: 'chained', label: 'Chained Macros', icon: <ChainedIcon />, color: '#7b1fa2' },
];

const Macros: React.FC = () => {
  const [macros, setMacros] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMacro, setEditingMacro] = useState<any | null>(null);
  const [form, setForm] = useState<any>({ name: '', description: '', type: 'static', content: '', sectionIds: [] });

  const fetchMacros = async (pageNum = 0, pageSize = 10) => {
    try {
      setLoading(true);
      const data = await apiService.getMacros(pageNum + 1, pageSize);
      setMacros(data.macros || data || []);
      setTotal(data.total || 0);
    } catch (e) {
      setMacros([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchSections = async () => {
    try {
      const data = await apiService.getSpecimenClassSections();
      setSections(data);
    } catch (e) {
      setSections([]);
    }
  };

  useEffect(() => {
    fetchMacros(page, rowsPerPage);
    fetchSections();
  }, [page, rowsPerPage]);

  const openAddDialog = () => {
    setEditingMacro(null);
    setForm({ name: '', description: '', type: 'static', content: '', sectionIds: [] });
    setOpenDialog(true);
  };

  const openEditDialog = (macro: any) => {
    setEditingMacro(macro);
    setForm({ ...macro, sectionIds: Array.isArray(macro.sections) ? macro.sections : [] });
    setOpenDialog(true);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setForm((prev: any) => ({ ...prev, [name!]: value }));
  };

  const handleSectionChange = (event: any) => {
    const { value } = event.target;
    const sectionIds = typeof value === 'string' ? value.split(',') : value;
    setForm((prev: any) => ({ ...prev, sectionIds: Array.isArray(sectionIds) ? sectionIds : [] }));
  };

  const handleSubmit = async () => {
    const payload = { ...form, sectionIds: form.sectionIds };
    if (editingMacro) {
      await apiService.updateMacro(editingMacro.id.toString(), payload);
    } else {
      await apiService.createMacro(payload);
    }
    setOpenDialog(false);
    fetchMacros(page, rowsPerPage);
  };

  const handleDelete = async (macro: any) => {
    if (!window.confirm('Delete this macro?')) return;
    await apiService.deleteMacro(macro.id.toString());
    fetchMacros(page, rowsPerPage);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getMacroTypeInfo = (type: string) => {
    return macroTypes.find(t => t.value === type) || macroTypes[0];
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e' }}>
            Macros
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage report macros and templates
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={openAddDialog}
          sx={{
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
            }
          }}
        >
          Add Macro
        </Button>
      </Box>

      {/* Macros Grid */}
      <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CodeIcon sx={{ fontSize: 18 }} />
                    Name
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <DescriptionIcon sx={{ fontSize: 18 }} />
                    Description
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CategoryIcon sx={{ fontSize: 18 }} />
                    Type
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SettingsIcon sx={{ fontSize: 18 }} />
                    Sections
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {macros.map(macro => {
                const typeInfo = getMacroTypeInfo(macro.type);
                return (
                  <TableRow 
                    key={macro.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: '#f8f9fa',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease-in-out'
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: '#fafafa'
                      }
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            bgcolor: typeInfo.color,
                            fontSize: '0.875rem'
                          }}
                        >
                          {macro.name?.charAt(0)?.toUpperCase() || 'M'}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {macro.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {macro.description || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        icon={typeInfo.icon}
                        label={typeInfo.label}
                        size="small"
                        sx={{ 
                          bgcolor: typeInfo.color,
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {Array.isArray(macro.sections) ? 
                          macro.sections.map((sid: number) => {
                            const section = sections.find((s: any) => s.id === sid);
                            return section ? (
                              <Chip 
                                key={sid}
                                label={section.name}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            ) : null;
                          }).filter(Boolean)
                        : (
                          <Typography variant="body2" fontSize="0.75rem" color="text.secondary">
                            No sections
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton 
                          onClick={() => openEditDialog(macro)}
                          size="small"
                          sx={{ 
                            color: '#1976d2',
                            '&:hover': { 
                              backgroundColor: 'rgba(25, 118, 210, 0.1)',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDelete(macro)}
                          size="small"
                          sx={{ 
                            color: '#d32f2f',
                            '&:hover': { 
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            borderTop: '1px solid #e0e0e0',
            '& .MuiTablePagination-toolbar': {
              paddingLeft: 2,
              paddingRight: 2,
            }
          }}
        />
      </Card>

      {/* Macro Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editingMacro ? 'Edit Macro' : 'Add Macro'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                name="name"
                value={form.name}
                onChange={handleFormChange}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                value={form.description}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={form.type}
                  onChange={(e) => setForm((prev: any) => ({ ...prev, type: e.target.value }))}
                  input={<OutlinedInput label="Type" />}
                >
                  {macroTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Content"
                name="content"
                value={form.content}
                onChange={handleFormChange}
                fullWidth
                multiline
                minRows={4}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Sections</InputLabel>
                <Select
                  multiple
                  value={Array.isArray(form.sectionIds) ? form.sectionIds : []}
                  onChange={handleSectionChange}
                  input={<OutlinedInput label="Sections" />}
                  renderValue={(selected) =>
                    (Array.isArray(selected) ? selected : []).map(sid => sections.find((s: any) => s.id === sid)?.name).filter(Boolean).join(', ')
                  }
                >
                  {sections.map((section: any) => (
                    <MenuItem key={section.id} value={section.id}>
                      <Checkbox checked={Array.isArray(form.sectionIds) ? form.sectionIds.indexOf(section.id) > -1 : false} />
                      <ListItemText primary={section.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
              }
            }}
          >
            {editingMacro ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Macros; 